// @ts-strict-ignore
import { useCallback } from 'react';
import { useSnackBar } from '@usealloy/component-library';
import type { WorkflowState } from 'flow/store/redux/state';
import type { SnippetVersion } from '~/dashboard/views/workflows/service/snippets/types';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import { scrubServiceCredentials } from '../MagicImport/helpers';
import cloneDeep from 'lodash/cloneDeep';
import { ActionType } from 'flow/store/engine/constants';
import { regeneratePublicIds } from 'flow/store/engine/regenerateIds';
import { snippetVersionSchema } from 'flow/service/snippets/schema';
import { ZodError } from 'zod';
import { useAppDispatch } from '~/store/reduxHooks';
import { insertSnippetWithLoadingThunk } from 'flow/store/redux/thunks/snippets';
import { createSnippetVersion } from 'flow/service/snippets/snippets';
import { useEditorAccess } from 'flow/editor/context';
import { EditorMode } from 'flow/editor/context/constants';

type UseCopyPasteHandlersParams = {
  workflowData: WorkflowState['workflowData'];
  workflowProps: WorkflowState['workflowProps'];
};

type UseCopyPasteHandlersReturn = {
  handleCopy: () => Promise<void>;
  handlePaste: () => Promise<void>;
};

/**
 * Filters workflow props to only include items that are actually used by actions in the snippet.
 */
export function filterPropsUsedByActions({
  actions,
  thresholds,
  workflowProps,
}: {
  actions: WorkflowState['workflowData']['actions'];
  thresholds: WorkflowState['workflowData']['thresholds'];
  workflowProps: WorkflowState['workflowProps'];
}) {
  const actionsArray = Object.values(actions);
  const thresholdsArray = Object.values(thresholds);

  // Collect identifiers for each prop type used in actions
  const usedServiceInternalNames = new Set<string>();
  const usedTagNames = new Set<string>();
  const usedOutcomeNames = new Set<string>();
  const usedPublishedAttributeTokens = new Set<string>();
  const usedReasonCodeGroupNames = new Set<string>();

  actionsArray.forEach(action => {
    switch (action.type) {
      case ActionType.SERVICE:
        if (action.service?.internalName) {
          usedServiceInternalNames.add(action.service.internalName);
        }
        break;

      case ActionType.TAG:
        if (action.tag?.name) {
          usedTagNames.add(action.tag.name);
        }
        break;

      case ActionType.OUTCOME:
        if (action.outcome?.name) {
          usedOutcomeNames.add(action.outcome.name);
        }
        break;

      case ActionType.INPUT:
        if (action.input?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.input.publishedAttributeToken);
        }
        break;

      case ActionType.OUTPUT:
        if (action.output?.publishedAttributeToken) {
          usedPublishedAttributeTokens.add(action.output.publishedAttributeToken);
        }
        break;

      default:
        break;
    }
  });


  thresholdsArray.forEach(threshold => {
    if (threshold.right?.reasonCodeGroup) {
      usedReasonCodeGroupNames.add(threshold.right.reasonCodeGroup.name);
    }
  });


  return {
    services: Object.values(workflowProps.services).filter(
      service => usedServiceInternalNames.has(service.internal_name),
    ),
    tags: workflowProps.tags.filter(
      tag => usedTagNames.has(tag.tag_name),
    ),
    outcomes: workflowProps.outcomes.filter(
      outcome => usedOutcomeNames.has(outcome.outcome),
    ),
    publishedAttributes: workflowProps.publishedAttributes.filter(
      pa => usedPublishedAttributeTokens.has(pa.attributeToken),
    ),
    reasonCodeGroups: workflowProps.reasonCodeGroups.filter(
      rcg => usedReasonCodeGroupNames.has(rcg.name),
    ),
  };
}

export function useCopyPasteHandlers({
  workflowData,
  workflowProps,
}: UseCopyPasteHandlersParams): UseCopyPasteHandlersReturn {
  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();

  const { setEditorMode } = useEditorAccess();

  const handleCopy = useCallback(async () => {
    try {
      const { actionsWithNoCredentials, impactedServiceActions } = scrubServiceCredentials({
        actions: cloneDeep(workflowData.actions),
        services: workflowProps.services,
      });

      const filteredProps = filterPropsUsedByActions({
        actions: actionsWithNoCredentials,
        thresholds: workflowData.thresholds,
        workflowProps,
      });

      const snippet: SnippetVersion = createSnippetVersion({
        actions: actionsWithNoCredentials,
        thresholds: workflowData.thresholds,
        dataRoutes: workflowData.dataRoutes,
        tags: filteredProps.tags,
        outcomes: filteredProps.outcomes,
        services: filteredProps.services,
        publishedAttributes: filteredProps.publishedAttributes,
        reasonCodeGroups: filteredProps.reasonCodeGroups,
      });

      const remappedSnippet = regeneratePublicIds(snippet);

      await navigator.clipboard.writeText(JSON.stringify(remappedSnippet));

      addSnackBar({
        duration: 5_000,
        title: 'Successfully copied workflow to clipboard.',
        type: 'success',
      });

      if (impactedServiceActions?.length > 0) {
        addSnackBar({
          duration: 5_000,
          title: `The service credentials in the following service nodes are removed in the workflow copied in the clipboard: ${impactedServiceActions.join(', ')}`,
          type: 'success',
        });
      }
    } catch (error) {
      captureExceptionsToSentry(error);
      addSnackBar({
        duration: 2_000,
        title: 'Something went wrong while copying the workflow data.',
        type: 'danger',
      });
    }
  }, [workflowData, workflowProps, addSnackBar]);

  const handlePaste = useCallback(async () => {
    const text = await navigator.clipboard.readText();
    let snippet: SnippetVersion;
    try {
      const object = JSON.parse(text);
      // todo BUILD-3979 - finalize the Snippet schema
      /* @ts-expect-error */
      snippet = snippetVersionSchema.parse(object);
      setEditorMode(EditorMode.ReadOnly);
    } catch (err) {
      if (err instanceof ZodError || err instanceof SyntaxError) {
        addSnackBar({
          duration: 2_000,
          title: 'Cannot paste invalid workflow data',
          type: 'danger',
        });
      }
    }
    if (snippet) {
      try {
        await dispatch(insertSnippetWithLoadingThunk({ snippet }));
        addSnackBar({
          duration: 2_000,
          title: 'Successfully pasted workflow from clipboard.',
          type: 'success',
        });
      } catch (error) {
        addSnackBar({
          duration: 2_000,
          title: 'Failed to paste workflow. Please try again.',
          type: 'danger',
        });
      }
    }
  }, [dispatch, addSnackBar, setEditorMode]);

  return {
    handleCopy,
    handlePaste,
  };
}

